/**
 * Straightening a photo: a small rotation inside the photo's own frame.
 *
 * The frame keeps its width and height, so a 4:5 image stays 4:5. Rotating leaves four
 * empty corner wedges. What happens to them depends on what the image is:
 *
 * - A photo with a real background turns as a whole and is zoomed in until the wedges
 *   are gone.
 * - A photo already padded to 4:5 sits between two white bars. Only the photo's own
 *   rectangle turns and zooms; the bars stay put. "Process All" pads every photo, so
 *   this is the usual state of a gallery photo in the editor.
 * - A cutout on a blank field turns as a whole and is not zoomed. Its wedges are filled
 *   white, which is invisible there, and a zoom would clip a product near the edge.
 *
 * Pure functions only, no canvas and no DOM. The canvas wrapper lives in imageProcessing.js.
 */

import { findContentBounds, hasBlankCorners, isBlankAt } from './imageBounds'

/** The slider's reach either side of level, in degrees. */
export const STRAIGHTEN_MAX_DEGREES = 10

export const STRAIGHTEN_STEP_DEGREES = 0.1

const WHOLE_FRAME = { x0: 0, y0: 0, x1: 1, y1: 1 }

/**
 * How far to zoom a rotated image so it still covers its own frame.
 *
 * @param {number} width
 * @param {number} height
 * @param {number} degrees Tilt in either direction.
 * @returns {number} Scale factor, 1 when level.
 */
export function coverScale(width, height, degrees) {
    const rad = (Math.abs(degrees) * Math.PI) / 180
    const longOverShort = Math.max(width / height, height / width)
    return Math.cos(rad) + longOverShort * Math.sin(rad)
}

/**
 * Which part of the frame turns, and whether it zooms. Edges are fractions of the
 * frame, so a region read from a thumbnail applies to the full-size image.
 *
 * @param {{data: Uint8ClampedArray|number[], width: number, height: number}} sample
 * @returns {{x0: number, y0: number, x1: number, y1: number, zoom: boolean}}
 */
export function findStraightenRegion(sample) {
    if (!hasBlankCorners(sample)) return { ...WHOLE_FRAME, zoom: true }
    const photo = findPaddedPhoto(sample)
    return photo ? { ...photo, zoom: true } : { ...WHOLE_FRAME, zoom: false }
}

/**
 * The pixel box that turns, and its zoom, for one image at one angle.
 *
 * @param {{width: number, height: number, degrees: number, region: object}} input
 * @returns {{box: {x: number, y: number, width: number, height: number}, scale: number}}
 */
export function straightenLayout({ width, height, degrees, region }) {
    const x = Math.round(region.x0 * width)
    const y = Math.round(region.y0 * height)
    const box = {
        x,
        y,
        width: Math.round(region.x1 * width) - x,
        height: Math.round(region.y1 * height) - y,
    }
    return { box, scale: region.zoom ? coverScale(box.width, box.height, degrees) : 1 }
}

/**
 * CSS for a live preview that matches what the canvas will draw.
 *
 * @param {{degrees: number, frame: {width: number, height: number, region: object}|null}} input
 *   `frame` is null until the image has been read, or when it cannot be read.
 * @returns {{transform?: string, transformOrigin?: string, mask?: object|null}}
 *   `mask` is the photo's own rectangle when only that part turns; everything outside
 *   it must be painted white.
 */
export function straightenPreview({ degrees, frame }) {
    if (degrees === 0) return {}
    if (!frame) return { transform: `rotate(${degrees}deg)` }

    const { region } = frame
    const { scale } = straightenLayout({ ...frame, degrees })
    const turnsWholeFrame = region.x0 === 0 && region.y0 === 0 && region.x1 === 1 && region.y1 === 1
    return {
        transform: `rotate(${degrees}deg) scale(${scale})`,
        transformOrigin: `${percent((region.x0 + region.x1) / 2)} ${percent((region.y0 + region.y1) / 2)}`,
        mask: turnsWholeFrame ? null : {
            left: percent(region.x0),
            top: percent(region.y0),
            width: percent(region.x1 - region.x0),
            height: percent(region.y1 - region.y0),
        },
    }
}

function percent(fraction) {
    return `${fraction * 100}%`
}

/**
 * The rectangle of a photo that sits between two blank bars, or null.
 *
 * A padded photo reaches both frame edges on exactly one axis and has picture in all
 * four of its own corners. A cutout that happens to reach both edges has empty corners.
 */
function findPaddedPhoto(sample) {
    const bounds = findContentBounds(sample)
    if (!bounds) return null

    const { width, height } = sample
    const spansWidth = bounds.left === 0 && bounds.right === width - 1
    const spansHeight = bounds.top === 0 && bounds.bottom === height - 1
    if (spansWidth === spansHeight) return null

    // The pixel on a bar-side edge is a blend of bar and photo, so step one inside.
    const inner = {
        left: spansWidth ? bounds.left : bounds.left + 1,
        right: spansWidth ? bounds.right : bounds.right - 1,
        top: spansHeight ? bounds.top : bounds.top + 1,
        bottom: spansHeight ? bounds.bottom : bounds.bottom - 1,
    }
    if (inner.right <= inner.left || inner.bottom <= inner.top) return null

    const cornersShowPicture = [
        [inner.left, inner.top], [inner.right, inner.top],
        [inner.left, inner.bottom], [inner.right, inner.bottom],
    ].every(([x, y]) => !isBlankAt(sample, x, y))
    if (!cornersShowPicture) return null

    return {
        x0: inner.left / width,
        x1: spansWidth ? 1 : (inner.right + 1) / width,
        y0: inner.top / height,
        y1: spansHeight ? 1 : (inner.bottom + 1) / height,
    }
}
